import {
  init_buffer,
  init_define_environment,
  init_dirname,
  init_process
} from "./chunk-S7VLD5KW.js";

// libs/core/src/lib/cache/public-api.ts
init_define_environment();
init_dirname();
init_buffer();
init_process();

// libs/core/src/lib/cache/time-cache.ts
init_define_environment();
init_dirname();
init_buffer();
init_process();
var TimeCache = class {
  constructor(ttlMs, autoClean = false) {
    this.ttlMs = ttlMs;
    this.autoClean = autoClean;
    this.cache = /* @__PURE__ */ new Map();
    this.enabledAutoClean();
  }
  /**
   * Set a key-value pair in the cache.
   *
   * @param {Key} key - The key of the pair to be set.
   * @param {Value} value - The value of the pair to be set.
   */
  set(key, value) {
    this.cache.set(key, {
      value,
      timestamp: Date.now()
    });
  }
  /**
   * Checks if the cache has a record for the given key and if it is still within the time-to-live (TTL) period.
   * @param {Key} key - The key to check the cache for.
   * @return {boolean} - Returns true if the cache has a record for the key within the TTL period, otherwise false.
   */
  get(key) {
    const record = this.cache.get(key);
    if (!record) return null;
    if (Date.now() - record.timestamp > this.ttlMs) {
      this.cache.delete(key);
      return null;
    }
    return record.value;
  }
  /**
   * Checks if the cache has a record for the given key and if it is still within the time-to-live (TTL) period.
   * @param {Key} key - The key to check the cache for.
   * @return {boolean} - Returns true if the cache has a record for the key within the TTL period, otherwise false.
   */
  has(key) {
    const record = this.cache.get(key);
    if (!record) return false;
    if (Date.now() - record.timestamp > this.ttlMs) {
      this.cache.delete(key);
      return false;
    }
    return true;
  }
  delete(key) {
    return this.cache.delete(key);
  }
  clear() {
    this.cache.clear();
    if (!this.autoCleanTimer) return;
    clearInterval(this.autoCleanTimer);
  }
  /**
  * @deprecated
  * */
  size() {
    return 0;
  }
  enabledAutoClean() {
    if (!this.autoClean) return;
    this.autoCleanTimer = setInterval(() => {
      for (const key of [...this.cache.keys()]) {
        this.get(key);
      }
    }, this.ttlMs);
  }
};

// libs/core/src/lib/cache/queue-cache.ts
init_define_environment();
init_dirname();
init_buffer();
init_process();
var QueueCache = class {
  /**
   * Creates a new instance of the QueueCache class.
   *
   * @constructor
   * @param {number} bufferSize - The size of the buffer.
   */
  constructor(bufferSize) {
    this.bufferSize = bufferSize;
    this.cache = /* @__PURE__ */ new Map();
    this.indexes = /* @__PURE__ */ new Map();
    this.lastIndex = 0;
  }
  /**
   * Add a key-value pair to the cache.
   * If the key already exists in the cache, nothing will be done.
   * If the cache is full, the least recently used item will be removed before adding the new item.
   *
   * @param {Key} key - The key to be added to the cache.
   * @param {Value} value - The value associated with the key to be added to the cache.
   *
   * @return {void}
   */
  set(key, value) {
    if (this.has(key)) {
      return;
    }
    if (this.cache.size >= this.bufferSize) {
      const min = Math.min(...this.indexes.keys());
      const key2 = this.indexes.get(min);
      this.indexes.delete(min);
      this.cache.delete(key2);
      return;
    }
    const index = this.lastIndex++;
    this.indexes.set(index, key);
    this.cache.set(key, value);
  }
  /**
   * Retrieves the value associated with the given key from the cache.
   *
   * @param {Key} key - The key to retrieve the value for.
   * @returns {Value | null} The value associated with the given key, or null if the key is not found in the cache.
   */
  get(key) {
    return this.cache.get(key) ?? null;
  }
  /**
   * Checks if the cache has the specified key.
   *
   * @param {Key} key - The key to check in the cache.
   * @returns {boolean} Returns true if the cache has the key, otherwise returns false.
   */
  has(key) {
    return this.cache.has(key);
  }
  delete(key) {
    return this.cache.delete(key);
  }
  /**
   * Clears the cache storage.
   *
   * @return {void}
   */
  clear() {
    this.cache.clear();
    this.indexes.clear();
    this.lastIndex = 0;
  }
  size() {
    return this.cache.size;
  }
};

// libs/core/src/lib/cache/long-time-cache.ts
init_define_environment();
init_dirname();
init_buffer();
init_process();
import { storage, JsonParser } from "@one-inch-community/core/storage";
var LongTimeCache = class {
  /**
   * Creates an instance of LongTimeCache.
   * @param {string} storageKey - The key used to store the cache in localStorage.
   * @param {number} ttlDays - The TTL (Time To Live) for cache items in days.
   */
  constructor(storageKey, ttlDays) {
    this.storageKey = storageKey;
    this.ttlDays = ttlDays;
    /** @private @readonly @type {Map<Key, LongTimeCacheData<Value>>} */
    this.cache = /* @__PURE__ */ new Map();
    const data = storage.get(this.storageKeyAndVersion, JsonParser);
    this.cache = new Map(data);
  }
  get storageKeyAndVersion() {
    return [this.storageKey, "v2"].join(":");
  }
  /**
   * Sets a value in the cache.
   * @param {Key} key - The key under which the value is stored.
   * @param {Value} value - The value to be cached.
   */
  set(key, value) {
    this.cache.set(key, { value, timestamp: Date.now() });
    this.updatePersistence();
  }
  /**
   * Gets a value from the cache.
   * @param {Key} key - The key of the value to retrieve.
   * @returns {Value | null} - The cached value, or null if not found or expired.
   */
  get(key) {
    this.checkCacheData(key);
    return this.cache.get(key)?.value ?? null;
  }
  /**
   * Checks if a key exists in the cache.
   * @param {Key} key - The key to check.
   * @returns {boolean} - True if the key exists and is not expired, otherwise false.
   */
  has(key) {
    this.checkCacheData(key);
    return this.cache.has(key);
  }
  /**
   * Deletes a value from the cache.
   * @param {Key} key - The key of the value to delete.
   * @returns {boolean} - True if the value existed and was deleted, otherwise false.
   */
  delete(key) {
    const state = this.cache.delete(key);
    this.updatePersistence();
    return state;
  }
  /**
   * Clears the cache.
   */
  clear() {
    this.cache.clear();
    this.updatePersistence();
  }
  /**
   * Gets the size of the cache.
   * @returns {number} - The number of items in the cache.
   */
  size() {
    let isDirty = false;
    this.cache.forEach((_, key) => {
      const state = this.checkCacheData(key, true);
      if (state && !isDirty) {
        isDirty = true;
      }
    });
    if (isDirty) {
      this.updatePersistence();
    }
    return this.cache.size;
  }
  /**
   * Checks if a cached item is expired.
   * @private
   * @param {Key} key - The key of the item to check.
   * @param {boolean} [skipUpdatePersistence=false] - Whether to skip updating persistence.
   * @returns {boolean} - True if the item was expired and deleted, otherwise false.
   */
  checkCacheData(key, skipUpdatePersistence) {
    const data = this.cache.get(key);
    if (!data) return false;
    let isDirty = false;
    if (Date.now() - data.timestamp > this.ttlDays * 864e5) {
      this.cache.delete(key);
      isDirty = true;
      if (!(skipUpdatePersistence ?? false)) {
        this.updatePersistence();
      }
    }
    return isDirty;
  }
  /**
   * Updates the persistent storage with the current cache state.
   * @private
   */
  updatePersistence() {
    storage.set(this.storageKeyAndVersion, Array.from(this.cache.entries()));
  }
};

// libs/core/src/lib/cache/long-time-async-cache.ts
init_define_environment();
init_dirname();
init_buffer();
init_process();
var version = 1;
async function getDexie(storageKey) {
  const Dexie = await import("./import-wrapper-KNBXC6SG.js").then((m) => m.Dexie);
  const db = new Dexie("one-inch-long-time-async-cache_" + storageKey);
  db.version(version).stores({
    cache: [
      "key",
      "value",
      "timestamp"
    ].join(", ")
  });
  return Reflect.get(db, "cache");
}
var LongTimeAsyncCache = class {
  constructor(storageKey, ttlDays) {
    this.storageKey = storageKey;
    this.ttlDays = ttlDays;
  }
  async set(key, value) {
    const db = await this.getDexie(this.storageKey);
    await db.put({ key: key.toLowerCase(), value, timestamp: Date.now() });
    this.cleanOldRecords().catch(console.error);
  }
  async get(key) {
    const _key = key.toLowerCase();
    const db = await this.getDexie(this.storageKey);
    const result = await db.filter((item) => Date.now() - item.timestamp < this.ttlDays * 864e5 && item.key === _key).first();
    this.cleanOldRecords().catch(console.error);
    return result?.value ?? null;
  }
  async getAll() {
    const db = await this.getDexie(this.storageKey);
    const result = await db.filter((item) => Date.now() - item.timestamp < this.ttlDays * 864e5).toArray();
    this.cleanOldRecords().catch(console.error);
    return result.map((record) => record.value);
  }
  async has(key) {
    const result = await this.get(key);
    return result !== null;
  }
  async delete(key) {
    try {
      const db = await this.getDexie(this.storageKey);
      await db.delete(key);
      this.cleanOldRecords().catch(console.error);
      return true;
    } catch {
      return false;
    }
  }
  async clear() {
    const db = await this.getDexie(this.storageKey);
    db.clear();
  }
  async size() {
    await this.cleanOldRecords().catch(console.error);
    const db = await this.getDexie(this.storageKey);
    return db.count();
  }
  async cleanOldRecords() {
    const db = await this.getDexie(this.storageKey);
    await db.where("timestamp").below(Date.now() - this.ttlDays * 864e5).delete();
  }
  async getDexie(storageKey) {
    if (this.storage !== void 0) {
      return this.storage;
    }
    this.storage = await getDexie(storageKey);
    return this.storage;
  }
};
export {
  LongTimeAsyncCache,
  LongTimeCache,
  QueueCache,
  TimeCache
};
//# sourceMappingURL=index.esm.js.map
